import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { auth, db } from '@/firebaseInit';
import { coll_admins } from '@/types/types_admin';
import { coll_partnerUsers, type PartnerUserType } from '@/types/types_partnerUser';
import { AuthContext, type AuthContextValue } from './AuthContext';

/**
 * Provides auth state plus the two roles the app knows about: Eighty Days
 * super-admin (`admins/{uid}`) and partner (`partnerUsers/{uid}`). Both
 * come from real-time listeners, so granting or revoking access in the Firebase
 * console — or disabling a partner account — takes effect without the user
 * signing out.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminReady, setAdminReady] = useState(false);
  const [partnerUser, setPartnerUser] = useState<PartnerUserType | null>(null);
  const [partnerReady, setPartnerReady] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
      // Reset role flags on every auth change; the effects below recompute
      // them for the new user (prevents a stale admin flash on account switch).
      setIsAdmin(false);
      setAdminReady(false);
      setPartnerUser(null);
      setPartnerReady(false);
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, coll_admins, user.uid),
      (snap) => {
        setIsAdmin(snap.exists());
        setAdminReady(true);
      },
      (err) => {
        console.error('Admin check failed', err);
        setIsAdmin(false);
        setAdminReady(true);
      },
    );
  }, [user]);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, coll_partnerUsers, user.uid),
      (snap) => {
        setPartnerUser(snap.exists() ? ({ ...snap.data(), id: snap.id } as PartnerUserType) : null);
        setPartnerReady(true);
      },
      (err) => {
        console.error('Partner check failed', err);
        setPartnerUser(null);
        setPartnerReady(true);
      },
    );
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAdmin,
      partnerUser,
      isPartner: partnerUser?.status === 'active',
      loading: !authReady || (!!user && (!adminReady || !partnerReady)),
      signInWithGoogle: () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        return signInWithPopup(auth, provider).then(() => {});
      },
      signInWithEmail: (email, password) =>
        signInWithEmailAndPassword(auth, email.trim(), password).then(() => {}),
      createAccountWithEmail: async (email, password, displayName) => {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        const name = displayName.trim();
        if (name) await updateProfile(cred.user, { displayName: name });
      },
      // `url` becomes the `continueUrl` on the emailed link, which is how
      // PageAuthAction knows where to send someone once they are done. Read off
      // the current origin so a reset requested from staging returns to staging.
      sendPasswordReset: (email) =>
        sendPasswordResetEmail(auth, email.trim(), { url: `${window.location.origin}/` }),
      signOutUser: () => signOut(auth),
    }),
    [user, isAdmin, partnerUser, authReady, adminReady, partnerReady],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
